/**
 * StandardCostPage — Sec 7F + Sec 8.
 *
 * Two tabs:
 *   1. Standards   — set/edit standard rates per (count, yarn type, period)
 *   2. Variance    — month-by-month variance vs actual cost, 8-bucket breakdown
 *                    by (count, yarn type)
 */
import { useState } from 'react'
import {
  App, Button, Card, Col, DatePicker, Drawer, Empty, Form, Grid, Input,
  InputNumber, Row, Select, Space, Spin, Table, Tabs, Tag, Typography,
} from 'antd'
import {
  CalculatorOutlined, EditOutlined, PlusOutlined, RiseOutlined, FallOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { useBreakpoint } = Grid
const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

interface StandardRate {
  id: string
  plant_id: string
  count_ne: string
  yarn_type: string
  effective_from: string
  effective_to: string | null
  is_active: boolean
  std_rm_cost_per_kg: string | null
  std_waste_pct: string | null
  std_realisation_pct: string | null
  std_power_kwh_per_kg: string | null
  std_power_rate_per_kwh: string | null
  std_labor_per_kg: string | null
  std_overhead_per_kg: string | null
  std_packing_per_kg: string | null
  std_total_per_kg: string | null
  notes: string | null
}

interface VarianceLine {
  count_ne: string
  yarn_type: string
  output_kg: string
  std_total_per_kg: string | null
  actual_total_per_kg: string | null
  rm_price_variance: string
  rm_usage_variance: string
  waste_variance: string
  yield_variance: string
  power_variance: string
  labor_variance: string
  overhead_variance: string
  total_variance: string
  verdict: 'FAVOURABLE' | 'UNFAVOURABLE' | 'ON_TARGET'
}

interface VarianceReport {
  plant_id: string
  period_year: number
  period_month: number
  lines: VarianceLine[]
  total_output_kg: string
  total_variance: string
  favourable_total: string
  unfavourable_total: string
}

const fmt = (v: string | null | number | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })

export default function StandardCostPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <CalculatorOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Standard Costs &amp; Variance
      </Title>
      <Text type="secondary">
        Compare planned (standard) cost per kg with actual cost. Variance buckets: RM price, RM usage, waste, yield, power, labour, overhead.
      </Text>

      <Card style={{ marginTop: 16 }} styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select
              value={plantId} onChange={setPlantId} style={{ width: isMobile ? '100%' : 220 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="standards"
          items={[
            { key: 'standards', label: 'Standard Rates', children: <StandardsTab plantId={plantId} isMobile={isMobile} /> },
            { key: 'variance',  label: 'Variance Report', children: <VarianceTab plantId={plantId} isMobile={isMobile} /> },
          ]}
        />
      </Card>
    </div>
  )
}

// ─── Standards Tab ───────────────────────────────────────────────────────────
function StandardsTab({ plantId, isMobile }: { plantId: string; isMobile: boolean }) {
  const { message: msg } = App.useApp()
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<StandardRate | null>(null)
  const [form] = Form.useForm()

  const { data: rates = [], isLoading } = useQuery<StandardRate[]>({
    queryKey: ['standard-costs', plantId],
    queryFn: () => apiClient.get('/standard-costs', { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (vals: any) => apiClient.post('/standard-costs', { ...vals, plant_id: plantId }).then(r => r.data),
    onSuccess: () => { msg.success('Standard rate saved'); qc.invalidateQueries({ queryKey: ['standard-costs'] }); setDrawerOpen(false); form.resetFields() },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Save failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      apiClient.patch(`/standard-costs/${id}`, body).then(r => r.data),
    onSuccess: () => { msg.success('Updated'); qc.invalidateQueries({ queryKey: ['standard-costs'] }); setDrawerOpen(false) },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Update failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ yarn_type: 'ALL', effective_from: dayjs() })
    setDrawerOpen(true)
  }

  const openEdit = (r: StandardRate) => {
    setEditing(r)
    form.setFieldsValue({
      ...r,
      effective_from: r.effective_from ? dayjs(r.effective_from) : null,
      effective_to: r.effective_to ? dayjs(r.effective_to) : null,
    })
    setDrawerOpen(true)
  }

  const onSubmit = () => {
    form.validateFields().then(vals => {
      const body = {
        ...vals,
        effective_from: vals.effective_from?.format('YYYY-MM-DD'),
        effective_to: vals.effective_to?.format('YYYY-MM-DD') ?? null,
      }
      if (editing) {
        const { plant_id: _p, count_ne: _c, yarn_type: _y, effective_from: _ef, ...rest } = body
        updateMut.mutate({ id: editing.id, body: rest })
      } else {
        createMut.mutate(body)
      }
    })
  }

  if (isLoading) return <Spin />
  if (rates.length === 0) {
    return (
      <>
        <Empty description="No standard rates yet — set one to enable variance analysis">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Standard Rate</Button>
        </Empty>
        <RateDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} editing={editing}
          form={form} onSubmit={onSubmit} loading={createMut.isPending || updateMut.isPending}
          isMobile={isMobile} />
      </>
    )
  }

  return (
    <>
      <Row justify="end" style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Standard Rate</Button>
      </Row>
      {isMobile ? (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {rates.map(r => (
            <Card key={r.id} size="small" hoverable onClick={() => openEdit(r)}
              styles={{ body: { padding: 12 } }}>
              <Row justify="space-between" align="middle">
                <Col><Text strong style={NUM_STYLE}>{Number(r.count_ne).toFixed(0)}s {r.yarn_type}</Text></Col>
                <Col>{r.is_active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>}</Col>
              </Row>
              <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                {dayjs(r.effective_from).format('DD MMM YY')} → {r.effective_to ? dayjs(r.effective_to).format('DD MMM YY') : 'Open'}
              </div>
              <div style={{ marginTop: 8, ...NUM_STYLE, fontWeight: 600, fontSize: 16 }}>
                ₹ {fmt(r.std_total_per_kg)} / kg
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                RM ₹{fmt(r.std_rm_cost_per_kg)} · Power {fmt(r.std_power_kwh_per_kg, 2)} kWh · Waste {fmt(r.std_waste_pct, 1)}%
              </div>
            </Card>
          ))}
        </Space>
      ) : (
        <Table size="small" dataSource={rates} rowKey="id" pagination={{ pageSize: 25 }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Count', dataIndex: 'count_ne', render: (v: string) => `${Number(v).toFixed(0)}s` },
            { title: 'Type',  dataIndex: 'yarn_type' },
            { title: 'Effective', render: (_, r: StandardRate) => `${dayjs(r.effective_from).format('DD MMM YY')} → ${r.effective_to ? dayjs(r.effective_to).format('DD MMM YY') : 'Open'}` },
            { title: 'Active', dataIndex: 'is_active', render: (v: boolean) => v ? <Tag color="success">Yes</Tag> : <Tag>No</Tag> },
            { title: 'RM ₹/kg', dataIndex: 'std_rm_cost_per_kg', align: 'right' as const, render: (v: string) => fmt(v) },
            { title: 'Power kWh', dataIndex: 'std_power_kwh_per_kg', align: 'right' as const, render: (v: string) => fmt(v, 2) },
            { title: 'Labor ₹/kg', dataIndex: 'std_labor_per_kg', align: 'right' as const, render: (v: string) => fmt(v) },
            { title: 'OH ₹/kg', dataIndex: 'std_overhead_per_kg', align: 'right' as const, render: (v: string) => fmt(v) },
            { title: 'Waste %', dataIndex: 'std_waste_pct', align: 'right' as const, render: (v: string) => fmt(v, 1) },
            { title: 'Total ₹/kg', dataIndex: 'std_total_per_kg', align: 'right' as const,
              render: (v: string) => <Text strong style={NUM_STYLE}>{fmt(v)}</Text> },
            { title: '', render: (_, r: StandardRate) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />) },
          ]}
        />
      )}
      <RateDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} editing={editing}
        form={form} onSubmit={onSubmit} loading={createMut.isPending || updateMut.isPending}
        isMobile={isMobile} />
    </>
  )
}

function RateDrawer({ drawerOpen, setDrawerOpen, editing, form, onSubmit, loading, isMobile }: any) {
  return (
    <Drawer
      title={editing ? `Edit standard — ${Number(editing.count_ne).toFixed(0)}s ${editing.yarn_type}` : 'New Standard Rate'}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      width={isMobile ? '100%' : 540}
      placement={isMobile ? 'bottom' : 'right'}
      height={isMobile ? '90%' : undefined}
      footer={
        <Row justify="end" gutter={8}>
          <Col><Button onClick={() => setDrawerOpen(false)}>Cancel</Button></Col>
          <Col><Button type="primary" loading={loading} onClick={onSubmit}>Save</Button></Col>
        </Row>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="count_ne" label="Count (Ne)" rules={[{ required: true }]}>
              <InputNumber min={6} max={120} style={{ width: '100%' }} disabled={!!editing} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="yarn_type" label="Yarn Type" rules={[{ required: true }]}>
              <Select disabled={!!editing} options={[
                { value: 'ALL', label: 'ALL' },
                { value: 'CARDED', label: 'CARDED' },
                { value: 'COMBED', label: 'COMBED' },
                { value: 'COMPACT', label: 'COMPACT' },
                { value: 'SIRO', label: 'SIRO' },
                { value: 'COMBED_COMPACT', label: 'COMBED_COMPACT' },
              ]} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} disabled={!!editing} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="effective_to" label="Effective To (optional)">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="std_rm_cost_per_kg" label="RM Cost ₹/kg">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="std_waste_pct" label="Waste %">
              <InputNumber min={0} max={50} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="std_power_kwh_per_kg" label="Power kWh/kg">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="std_power_rate_per_kwh" label="Power Rate ₹/kWh">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="std_labor_per_kg" label="Labour ₹/kg">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="std_overhead_per_kg" label="Overhead ₹/kg">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="std_packing_per_kg" label="Packing ₹/kg">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="std_realisation_pct" label="Realisation %">
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

// ─── Variance Tab ────────────────────────────────────────────────────────────
function VarianceTab({ plantId, isMobile }: { plantId: string; isMobile: boolean }) {
  const [period, setPeriod] = useState<Dayjs>(dayjs().subtract(1, 'month'))
  const periodYear = period.year()
  const periodMonth = period.month() + 1

  const { data, isLoading } = useQuery<VarianceReport>({
    queryKey: ['variance-report', plantId, periodYear, periodMonth],
    queryFn: () => apiClient.get('/standard-costs/variance-report', {
      params: { plant_id: plantId, period_year: periodYear, period_month: periodMonth },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  return (
    <>
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col xs={24} md="auto">
          <DatePicker picker="month" value={period} onChange={v => v && setPeriod(v)}
            style={{ width: isMobile ? '100%' : 200 }} allowClear={false} />
        </Col>
      </Row>
      {isLoading ? <Spin /> : !data || data.lines.length === 0 ? (
        <Empty description={`No production output for ${period.format('MMM YYYY')} or no standard rates configured`} />
      ) : (
        <>
          {/* Summary card */}
          <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Output</Text>
                <div style={{ ...NUM_STYLE, fontSize: 18, fontWeight: 700 }}>
                  {fmt(data.total_output_kg, 0)} kg
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}
                style={{ borderColor: Number(data.total_variance) >= 0 ? '#b7eb8f' : '#ffa39e' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Total Variance</Text>
                <div style={{ ...NUM_STYLE, fontSize: 18, fontWeight: 700,
                  color: Number(data.total_variance) >= 0 ? '#52c41a' : '#cf1322' }}>
                  {Number(data.total_variance) >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  {' ₹'}{fmt(Math.abs(Number(data.total_variance)), 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Favourable</Text>
                <div style={{ ...NUM_STYLE, fontSize: 18, fontWeight: 700, color: '#52c41a' }}>
                  ₹{fmt(data.favourable_total, 0)}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Unfavourable</Text>
                <div style={{ ...NUM_STYLE, fontSize: 18, fontWeight: 700, color: '#cf1322' }}>
                  ₹{fmt(Math.abs(Number(data.unfavourable_total)), 0)}
                </div>
              </Card>
            </Col>
          </Row>

          <Table size="small" dataSource={data.lines} rowKey={(r) => `${r.count_ne}-${r.yarn_type}`}
            pagination={false} scroll={{ x: 'max-content' }}
            columns={[
              { title: 'Count', dataIndex: 'count_ne', render: (v: string) => `${Number(v).toFixed(0)}s` },
              { title: 'Type', dataIndex: 'yarn_type' },
              { title: 'Output kg', dataIndex: 'output_kg', align: 'right' as const, render: (v: string) => fmt(v, 0) },
              { title: 'Std ₹/kg', dataIndex: 'std_total_per_kg', align: 'right' as const, render: (v: string) => fmt(v) },
              { title: 'Actual ₹/kg', dataIndex: 'actual_total_per_kg', align: 'right' as const, render: (v: string) => fmt(v) },
              { title: 'RM Var', dataIndex: 'rm_price_variance', align: 'right' as const, render: (v: string) => fmt(v, 0) },
              { title: 'Power Var', dataIndex: 'power_variance', align: 'right' as const, render: (v: string) => fmt(v, 0) },
              { title: 'Labour Var', dataIndex: 'labor_variance', align: 'right' as const, render: (v: string) => fmt(v, 0) },
              { title: 'OH Var', dataIndex: 'overhead_variance', align: 'right' as const, render: (v: string) => fmt(v, 0) },
              { title: 'Total Var', dataIndex: 'total_variance', align: 'right' as const,
                render: (v: string) => <Text strong style={{ ...NUM_STYLE,
                  color: Number(v) >= 0 ? '#52c41a' : '#cf1322' }}>{fmt(v, 0)}</Text> },
              { title: '', dataIndex: 'verdict', render: (v: string) => (
                <Tag color={v === 'FAVOURABLE' ? 'success' : v === 'UNFAVOURABLE' ? 'error' : 'default'}>{v}</Tag>) },
            ]}
          />
        </>
      )}
    </>
  )
}
